import React, { useState } from 'react';
import { Card, CardContent } from '../../../../../../components/ui/card';
import { Input } from '../../../../../../components/ui/input';
import { Label } from '../../../../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../../../../components/ui/dialog';
import { Plus, Shield } from 'lucide-react';
import { DashboardProject } from '../../../../types/project';
import type { PipeSize, SetupState } from '../../../../types/setup';
import { mockBuckleArrestorSetup, mockBulkHeadSetup } from '../../../../mocks/setup';
import { Button } from '../../../../../../components/ui/button';

interface BulkHeadSetupProps {
  project: DashboardProject;
}

export const BulkHeadSetup: React.FC<BulkHeadSetupProps> = ({ project }) => {
  const [state, setState] = useState<SetupState>(mockBulkHeadSetup);

  const handleNumberOfPipeSizesChange = (value: string) => {
    const num = parseInt(value) || 0;
    setState(prev => ({
      ...prev,
      numberOfPipeSizes: num,
      pipeSizes: Array(num).fill(null).map((_, index) => {
        return prev.pipeSizes[index] || {
          size: '',
          wallThickness: '',
          buckleArrestors: '',
          numberOfPipes: '',
          pipeLength: '',
          numberOfForges: '',
          outputAssemblyLength: '',
          manufacturer: ''
        };
      })
    }));
  };

  const handlePipeSizeChange = (index: number, field: keyof PipeSize, value: string) => {
    setState(prev => ({
      ...prev,
      pipeSizes: prev.pipeSizes.map((pipe, i) => 
        i === index ? { ...pipe, [field]: value } : pipe
      )
    }));
  };

  const handleAddManufacturer = () => {
    if (state.newManufacturer.trim()) {
      setState(prev => ({
        ...prev,
        manufacturers: [...prev.manufacturers, prev.newManufacturer.trim()],
        newManufacturer: '',
        isAddingManufacturer: false
      }));
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-2 text-purple-800">
        <Shield className="h-5 w-5" />
        <h3 className="text-lg font-medium">Bulk Head Setup</h3>
      </div>
      
      {/* Number of Pipe Sizes Input */}
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <div className="w-full max-w-xs">
            <Label htmlFor="numberOfPipeSizes" className="text-base">
              Number of Pipe Sizes
            </Label>
            <Input
              id="numberOfPipeSizes"
              type="number"
              min="0"
              value={state.numberOfPipeSizes}
              onChange={(e) => handleNumberOfPipeSizesChange(e.target.value)}
              className="mt-2"
              placeholder="Enter number of pipe sizes"
            />
          </div>
        </div>
      </div>

      {/* Pipe Sizes Inputs */}
      {state.pipeSizes.length > 0 && (
        <div className="space-y-6">
          <h4 className="text-lg font-medium">Pipe Size Details</h4>
          {state.pipeSizes.map((pipe, index) => (
            <Card key={index} className="border border-gray-200">
              <CardContent className="p-4">
                <div className="space-y-4">
                  <h5 className="font-medium text-gray-700">Pipe {index + 1}</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor={`pipeSize-${index}`}>Pipe Size (inches)</Label>
                      <Input
                        id={`pipeSize-${index}`}
                        type="number"
                        min="0"
                        step="0.01"
                        value={pipe.size}
                        onChange={(e) => handlePipeSizeChange(index, 'size', e.target.value)}
                        className="mt-2"
                        placeholder="Enter pipe size"
                      />
                    </div>
                    <div>
                      <Label htmlFor={`wallThickness-${index}`}>Wall Thickness (inches)</Label>
                      <Input
                        id={`wallThickness-${index}`}
                        type="number"
                        min="0"
                        step="0.01"
                        value={pipe.wallThickness}
                        onChange={(e) => handlePipeSizeChange(index, 'wallThickness', e.target.value)}
                        className="mt-2"
                        placeholder="Enter wall thickness"
                      />
                    </div>
                    <div>
                      <Label htmlFor={`numberOfPipes-${index}`}>Number of Pipes</Label>
                      <Input
                        id={`numberOfPipes-${index}`}
                        type="number"
                        min="0"
                        value={pipe.numberOfPipes}
                        onChange={(e) => handlePipeSizeChange(index, 'numberOfPipes', e.target.value)}
                        className="mt-2"
                        placeholder="Enter number of pipes"
                      />
                    </div>
                    <div>
                      <Label htmlFor={`pipeLength-${index}`}>Length of Pipes (inches)</Label>
                      <Input
                        id={`pipeLength-${index}`}
                        type="number"
                        min="0"
                        step="0.01"
                        value={pipe.pipeLength}
                        onChange={(e) => handlePipeSizeChange(index, 'pipeLength', e.target.value)}
                        className="mt-2"
                        placeholder="Enter pipe length"
                      />
                    </div>
                    <div>
                      <Label htmlFor={`buckleArrestors-${index}`}>Number of Buckle Arrestors</Label>
                      <Input
                        id={`buckleArrestors-${index}`}
                        type="number"
                        min="0"
                        value={pipe.buckleArrestors}
                        onChange={(e) => handlePipeSizeChange(index, 'buckleArrestors', e.target.value)}
                        className="mt-2"
                        placeholder="Enter number of buckle arrestors"
                      />
                    </div>
                   
                    <div className="md:col-span-2">
                      <Label htmlFor={`outputAssemblyLength-${index}`}>Expected Output Assembly Length (inches)</Label>
                      <Input
                        id={`outputAssemblyLength-${index}`}
                        type="number"
                        min="0"
                        step="0.01"
                        value={pipe.outputAssemblyLength}
                        onChange={(e) => handlePipeSizeChange(index, 'outputAssemblyLength', e.target.value)}
                        className="mt-2"
                        placeholder="Enter expected output assembly length"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Manufacturer</Label>
                      <div className="flex gap-2 mt-2">
                        <Select
                          value={pipe.manufacturer}
                          onValueChange={(value) => handlePipeSizeChange(index, 'manufacturer', value)}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select manufacturer" />
                          </SelectTrigger>
                          <SelectContent>
                            {state.manufacturers.map((mfr) => (
                              <SelectItem key={mfr} value={mfr}>
                                {mfr}
                              </SelectItem>
                            ))}
                            <Dialog open={state.isAddingManufacturer} onOpenChange={(open) => setState(prev => ({ ...prev, isAddingManufacturer: open }))}>
                              <DialogTrigger asChild>
                                <Button
                                  className="flex items-center w-full px-2 py-1.5 text-sm hover:bg-gray-100 text-[#e04530]"
                                  variant="ghost"
                                >
                                  <Plus className="w-4 h-4 mr-2" />
                                  Add New Manufacturer
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Add New Manufacturer</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4 py-4">
                                  <div className="space-y-2">
                                    <Label>Manufacturer Name</Label>
                                    <Input
                                      value={state.newManufacturer}
                                      onChange={(e) => setState(prev => ({ ...prev, newManufacturer: e.target.value }))}
                                      placeholder="Enter manufacturer name"
                                    />
                                  </div>
                                  <div className="flex justify-end">
                                    <Button
                                      onClick={handleAddManufacturer}
                                      className="bg-[#e04530] text-white px-4 py-2 rounded-lg hover:bg-[#e04530]/90 transition-colors"
                                      disabled={!state.newManufacturer.trim()}
                                    >
                                      Add Manufacturer
                                    </Button>
                                  </div>
                                </div>
                              </DialogContent>
                            </Dialog>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}; 